import { useCallback, useMemo, useReducer, useRef } from 'react';

import { chatReducer, initialChatState } from './chatbot.reducer';
import { chatMessage, newMessageId } from './chatbot.helpers';
import { IReportCardChatAdapter, IReportCardChatRunResult } from './chatbot.types';
import { createReportCardChatAdapter } from './report-card-chat.adapter';
import { PROXY_PATH } from './chatbot.constants';
import { BASE_API_URL, HTTP_PROTOCOL } from '../../../lib/constants';

export interface IUseReportCardChatOptions {
  playerAssessmentId: string;
  token?: string;
  /** Bearer token forwarded to the proxy for authenticated users. */
  authToken?: string;
  /** Override the adapter factory (for testing). */
  adapter?: IReportCardChatAdapter;
}

export const useReportCardChat = (
  { playerAssessmentId, token, authToken, adapter: adapterOverride }: IUseReportCardChatOptions
) => {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const abortRef = useRef<AbortController | null>(null);

  const adapter = useMemo(() => {
    if (adapterOverride) return adapterOverride;
    return createReportCardChatAdapter({
      proxyUrl: `${HTTP_PROTOCOL}://${BASE_API_URL}${PROXY_PATH}`,
      context: { playerAssessmentId, token },
      authToken,
    });
  }, [adapterOverride, playerAssessmentId, token, authToken]);

  const send = useCallback(
    async (text: string): Promise<void> => {
      const trimmed = text.trim();
      if (!trimmed) return;
      // Cancel any in-flight stream before starting a new one — the panel UI
      // disables the send button while streaming, so this is belt-and-braces.
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const user = chatMessage('user', trimmed);
      const assistantId = newMessageId();
      dispatch({ type: 'send/start', userMessage: user, assistantId });

      try {
        let final = '';
        const history = state.messages;
        const generator = adapter.run({
          messages: [...history, user],
          abortSignal: controller.signal,
        });
        // Each yield is cumulative content; replace the placeholder with the latest text.
        // eslint-disable-next-line no-restricted-syntax
        for await (const result of generator as AsyncIterable<IReportCardChatRunResult>) {
          const cumulative = result.content
            .filter((p) => p.type === 'text')
            .map((p) => p.text)
            .join('');
          final = cumulative;
          dispatch({ type: 'send/chunk', assistantId, cumulativeText: cumulative });
        }
        dispatch({ type: 'send/done', assistantId, finalText: final });
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') {
          dispatch({ type: 'send/abort', assistantId });
          return;
        }
        console.error('useReportCardChat send failed:', err);
        const message = (err as Error)?.message
          || "Sorry — I couldn't send that message. Please try again.";
        dispatch({ type: 'send/error', assistantId, error: message });
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [adapter, state.messages]
  );

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    dispatch({ type: 'reset' });
  }, []);

  return {
    messages: state.messages,
    status: state.status,
    error: state.error,
    send,
    stop,
    reset,
    isStreaming: state.status === 'streaming' || state.status === 'sending',
  };
};
